<script setup lang="ts">
import { HgIcon, HgLink, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import AgentPackageTierBadge from '@/components/AgentCard/AgentPackageTierBadge.vue';
import { useAgentCardChipOverflow } from '@/composables/useAgentCardChipOverflow';
import {
  SERVICE_CHIPS,
  SPECIALIZATION_CHIPS,
  type ServiceChip,
} from '@/helpers/AgentServices/agentServiceChips';
import { generateAgencyProfilePagePath, generateAgentProfilePagePath } from '@/helpers/Url/Url';
import { AgentService, AgentSpecialization, type AgentProfile } from '@/types/agentProfile';

defineEmits(['agentCardClick']);

export interface AgentCardProps {
  agent: AgentProfile;
  language: HgSupportedLanguage;
}

const props = defineProps<AgentCardProps>();

const { t } = useI18n();

const avatarError = ref(false);
const onAvatarLoadFailure = () => {
  avatarError.value = true;
};

const agencyLogoError = ref(false);
const onAgencyLogoLoadFailure = () => {
  agencyLogoError.value = true;
};

const agentFullName = computed(() => `${props.agent.firstName} ${props.agent.lastName}`.trim());

const showAvatar = computed(() => !!props.agent.avatarUrl && !avatarError.value);
const showAgencyLogo = computed(() => !!props.agent.agencyLogoUrl && !agencyLogoError.value);
const showPackageTierBadge = computed(
  () =>
    props.agent.packageTier != null && ['RE_GOLD', 'RE_SILVER'].includes(props.agent.packageTier),
);

const hasBuyListings = computed(() => props.agent.propertiesBuyCount > 0);
const hasRentListings = computed(() => props.agent.propertiesRentCount > 0);
const hasSoldListings = computed(() => (props.agent.propertiesSoldCount ?? 0) > 0);

const hasListingsCount = computed(
  () => hasBuyListings.value || hasRentListings.value || hasSoldListings.value,
);

const totalListingsCount = computed(
  () =>
    props.agent.propertiesBuyCount +
    props.agent.propertiesRentCount +
    (props.agent.propertiesSoldCount ?? 0),
);

const listingsCountText = computed(() =>
  t('agentList.listings', { count: totalListingsCount.value }),
);

const listingsBreakdownText = computed(() =>
  [
    hasSoldListings.value ? t('agentList.sold', { count: props.agent.propertiesSoldCount }) : '',
    hasBuyListings.value ? t('agentList.forSale', { count: props.agent.propertiesBuyCount }) : '',
    hasRentListings.value ? t('agentList.forRent', { count: props.agent.propertiesRentCount }) : '',
  ]
    .filter(Boolean)
    .join(', '),
);

const agencyProfilePagePath = computed(() => {
  const { headAgencyId: customerId, profilePageUrlKeyword } = props.agent;

  if (!customerId || !profilePageUrlKeyword) {
    return null;
  }

  return generateAgencyProfilePagePath(props.language, profilePageUrlKeyword, customerId);
});

const serviceChips = computed<ServiceChip[]>(() => {
  const services = (props.agent.services ?? [])
    .filter((service): service is AgentService =>
      Object.values<string>(AgentService).includes(service),
    )
    .map((service) => ({ key: service, ...SERVICE_CHIPS[service] }));

  const specializations = (props.agent.specializations ?? [])
    .filter((specialization): specialization is AgentSpecialization =>
      Object.values<string>(AgentSpecialization).includes(specialization),
    )
    .map((specialization) => ({ key: specialization, ...SPECIALIZATION_CHIPS[specialization] }));

  return [...services, ...specializations];
});

const serviceChipCount = computed(() => serviceChips.value.length);
const serviceChipLabelKeys = computed(() => serviceChips.value.map((chip) => chip.labelKey));

const servicesContainerRef = useTemplateRef<HTMLElement>('servicesContainerRef');
const measureLayerRef = useTemplateRef<HTMLElement>('measureLayerRef');
const overflowMeasureElement = useTemplateRef<HTMLElement>('overflowMeasureElement');

const { measureOverflowCount, visibleChipCount, hiddenChipCount, hiddenServicesTitle } =
  useAgentCardChipOverflow(serviceChipCount, serviceChipLabelKeys, (labelKey) => t(labelKey), {
    servicesContainerRef,
    measureLayerRef,
    overflowMeasureElement,
  });
</script>

<template>
  <div :class="$style.agentCardWrapper" data-test="agent-card">
    <div :class="$style.agentCard">
      <div :class="$style.agentAvatar" data-test="agent-avatar">
        <img
          v-if="showAvatar"
          loading="lazy"
          :alt="agentFullName"
          :src="agent.avatarUrl || ''"
          @error="onAvatarLoadFailure"
        />
        <HgIcon v-else icon="PersonOutline" :themeClass="$style.agentAvatarIcon" />
      </div>
      <div :class="$style.agentInfo">
        <div :class="$style.agentProfile">
          <div :class="$style.agentName" :title="agentFullName" data-test="agent-name">
            {{ agentFullName }}
          </div>
          <div
            :class="$style.agentAgency"
            :title="`${t('agentList.expertAt')} ${agent.agencyName}`"
            data-test="agent-agency"
          >
            <span :class="$style.expertLabel">{{ t('agentList.expertAt') }}</span>
            <HgLink
              v-if="agencyProfilePagePath"
              :class="$style.agencyName"
              :href="agencyProfilePagePath"
              data-test="agency-name-link"
            >
              {{ agent.agencyName }}
            </HgLink>
            <span v-else :class="$style.agencyName" data-test="agency-name">
              {{ agent.agencyName }}
            </span>
          </div>
          <div v-if="agent.cityLocation" :class="$style.agentLocation" data-test="agent-location">
            <HgIcon icon="Pin" :themeClass="$style.agentCardIcon" />
            <span>{{ agent.cityLocation }}</span>
          </div>
          <div v-if="hasListingsCount" :class="$style.agentListings" data-test="agent-listings">
            <HgIcon icon="House" :themeClass="$style.agentCardIcon" />
            <span :class="$style.listingsCount">{{ listingsCountText }}</span>
            <span :class="$style.listingsBreakdown" data-test="agent-listings-breakdown">
              ({{ listingsBreakdownText }})
            </span>
          </div>
        </div>
        <div
          v-if="serviceChips.length"
          ref="servicesContainerRef"
          :class="$style.agentServices"
          data-test="agent-services"
        >
          <div
            ref="measureLayerRef"
            :class="$style.agentServicesMeasure"
            aria-hidden="true"
            data-test="agent-services-measure"
          >
            <span
              v-for="chip in serviceChips"
              :key="`measure-${chip.key}`"
              :class="$style.serviceChip"
              :data-test="`service-chip-measure-${chip.key}`"
            >
              <HgIcon :icon="chip.icon" :themeClass="$style.serviceChipIcon" />
              {{ t(chip.labelKey) }}
            </span>
            <span
              v-if="measureOverflowCount > 0"
              ref="overflowMeasureElement"
              :class="$style.serviceChip"
              data-test="service-chip-overflow-measure"
            >
              +{{ measureOverflowCount }}
            </span>
          </div>
          <div :class="$style.agentServicesVisible">
            <span
              v-for="(chip, index) in serviceChips"
              v-show="index < visibleChipCount"
              :key="chip.key"
              :class="$style.serviceChip"
              :data-test="`service-chip-${chip.key}`"
            >
              <HgIcon :icon="chip.icon" :themeClass="$style.serviceChipIcon" />
              {{ t(chip.labelKey) }}
            </span>
            <span
              v-if="hiddenChipCount > 0"
              :class="$style.serviceChip"
              :title="hiddenServicesTitle"
              :aria-label="hiddenServicesTitle"
              data-test="service-chip-overflow"
            >
              +{{ hiddenChipCount }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="showPackageTierBadge || showAgencyLogo"
        :class="$style.agentMeta"
        data-test="agent-meta"
      >
        <AgentPackageTierBadge v-if="showPackageTierBadge" :packageTier="agent.packageTier" />
        <div v-if="showAgencyLogo" :class="[$style.agencyLogo]" data-test="agency-logo">
          <img
            loading="lazy"
            :alt="agent.agencyName"
            :src="agent.agencyLogoUrl || ''"
            @error="onAgencyLogoLoadFailure"
          />
        </div>
      </div>
    </div>
    <a
      :class="$style.agentCardLink"
      :href="generateAgentProfilePagePath(language, agent)"
      :aria-label="agentFullName"
      data-test="agent-card-link"
      @click="$emit('agentCardClick')"
    />
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

// Grid when card is wide enough for avatar + info + meta, independent of viewport.
$agent-card-grid-min-width: calc($space-1000 + $space-600 + 7.125rem + 12rem);

.agentCardIcon {
  @include color-icon($color-grey-90);

  svg {
    width: $space-100; // 16px
    height: $space-100;
  }
}

.agentAvatarIcon {
  @include color-icon($color-grey-70);

  svg {
    width: $space-400; // 64px
    height: $space-400;
  }

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    svg {
      width: $space-500; // 80px
      height: $space-500;
    }
  }
}

.serviceChipIcon {
  @include color-icon($color-grey-90);

  svg {
    width: $space-100; // 16px
    height: $space-100;
  }
}

.agentCardWrapper {
  position: relative;
  display: block;
  overflow: hidden;
  border-radius: $border-radius-l;
  box-shadow: $elevation-minimal;
  container-type: inline-size;
  container-name: agent-card;

  &:hover,
  &:visited,
  &:active {
    text-decoration: none;
  }
}

.agentCardLink {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.agentCard {
  display: flex;
  flex-direction: column;
  background-color: $color-white;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    display: grid;
    grid-template-columns: calc($space-1000 + $space-600) 1fr auto;
  }
}

.agentAvatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc($space-1000 * 2);
  overflow: hidden;
  background-color: $color-grey-50;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > svg {
    width: 100%;
    height: 100%;
  }

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    align-self: stretch;
    width: calc($space-1000 + $space-600);
    height: calc($space-1000 + $space-600);
  }
}

.agentInfo {
  display: flex;
  flex-direction: column;
  gap: $space-150; // 24px per Figma Agent Card v2
  min-width: 0;
  padding: $space-100;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    justify-content: space-between;
    padding: $space-100 $space-200;
  }
}

.agentProfile {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  min-width: 0;
}

.agentName {
  overflow: hidden;
  color: $color-grey-120;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include heading-3;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    @include heading-2;
  }
}

.agentAgency {
  position: relative;
  z-index: 1;
  display: flex;
  gap: $space-25;
  min-width: 0;
  overflow: hidden;
  color: $color-grey-90;
  white-space: nowrap;

  @include body-3-semi-bold;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    @include body-2-semi-bold;
  }

  .expertLabel {
    flex-shrink: 0;
  }

  .agencyName {
    // domus HgLink uses inline-flex; block lets the flex item shrink and ellipsize
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    :deep(> span) {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.agentLocation,
.agentListings {
  display: flex;
  gap: $space-25;
  align-items: center;

  span {
    color: $color-grey-90;
  }
}

.agentLocation span {
  @include body-3-semi-bold;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    @include body-2-semi-bold;
  }
}

.listingsCount {
  @include body-3-semi-bold;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    @include body-2-semi-bold;
  }
}

.listingsBreakdown {
  @include body-3;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    @include body-2;
  }
}

.agentServices {
  position: relative;

  .serviceChip {
    display: inline-flex;
    gap: $space-25;
    align-items: center;
    padding: $space-25;
    color: $color-grey-90;
    background-color: $color-grey-30;
    border-radius: $border-radius;

    @include body-3-semi-bold;
  }
}

.agentServicesMeasure {
  position: absolute;
  inset: 0;
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  pointer-events: none;
  visibility: hidden;
}

.agentServicesVisible {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
}

.agentMeta {
  display: flex;
  gap: $space-50;
  align-items: center;
  justify-content: space-between;
  padding: $space-100;
  border-top: 1px solid $color-grey-60;

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    flex-direction: column;
    align-items: flex-end;
    align-self: stretch;
    justify-content: space-between;
    padding: $space-100 $space-100 $space-100 0;
    border-top: 0;
  }
}

.agencyLogo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6rem;
  height: 3.75rem;
  padding: $space-50;
  margin-left: auto;
  border: 1px solid $color-grey-60;
  border-radius: $space-25;

  img {
    max-width: 100%;
    max-height: 100%;
  }

  @container agent-card (min-width: #{$agent-card-grid-min-width}) {
    width: 7.125rem;
    height: 4.4375rem;
    margin-top: auto;
  }
}
</style>
